// Singleton E2eeCrypto instance + helpers for the rest of the app.
// Lazily initialized on first use; initIdentity() is idempotent and fast
// after the first run (keys cached in IndexedDB).

import { apiRequest } from 'mastodon/api';
import { me } from 'mastodon/initial_state';

import {
  E2eeCrypto,
  type PlaintextPayload,
  type FanoutEntry,
  type FanoutOpts,
  type GroupDecryptResult,
  type IdentityChangeAllowance,
  type IdentityChangeOpts,
  type SiblingBackfillFanout,
  isWasmSupported,
} from './crypto';
export type {
  PlaintextPayload,
  ReplyTo,
  SiblingBackfillFanout,
  FanoutOpts,
  GroupDecryptResult,
  IdentityChangeAllowance,
  IdentityChangeOpts,
} from './crypto';
export { isWasmSupported } from './crypto';
import { SignalProtocolStore } from './signal_store';

export interface SafetyNumber {
  displayable: string;
  scannable: ArrayBuffer;
}

export interface IdentityKeyChange {
  changed: boolean;
  oldKey?: string;
  newKey?: string;
  deviceId?: number;
  newDevices?: number[];
}

// Parse a server iso8601 timestamp that may carry MICROSECOND fractions
// (Rails `updated_at`), which Date.parse rejects on some engines. Returns
// NaN for unparseable input.
function parseServerTimestamp(iso: string): number {
  const normalized = iso.replace(/(\.\d{3})\d+(?=[zZ+-]|$)/, '$1');
  return Date.parse(normalized);
}

let _instance: E2eeCrypto | null = null;
let _initPromise: Promise<void> | null = null;

export function getCrypto(): E2eeCrypto {
  if (!_instance) {
    const dbName = `mastodon-e2ee-${me ?? 'anon'}`;
    const store = new SignalProtocolStore(dbName);
    _instance = new E2eeCrypto(
      apiRequest as unknown as ConstructorParameters<typeof E2eeCrypto>[0],
      store,
      me ? { accountId: String(me) } : undefined,
    );
  }
  return _instance;
}

export async function getMyDeviceId(): Promise<number | undefined> {
  await ensureE2eeReady();
  return getCrypto().getMyDeviceId();
}

export async function ensureE2eeReady(): Promise<void> {
  if (!isWasmSupported()) {
    throw new Error(
      "Your browser doesn't support WebAssembly, which is required for encrypted messaging. Please use a modern browser (Chrome 57+, Firefox 52+, Safari 11+, Edge 16+).",
    );
  }
  if (!_initPromise) {
    _initPromise = (async () => {
      try {
        const state = await detectE2eeSetupState();
        if (state !== 'ready') {
          throw new Error('e2ee_provisioning_required');
        }
        await getCrypto().initIdentity();
        // Persist the post-init state promptly: init may have just hydrated a
        // backup AND/OR healed the prekey pool (init-time cross-check). If the
        // device is lost before the next message activity, the next restore
        // would get a pre-heal backup whose pool diverges again — the
        // perpetual-poison loop seen in e2e (heal storm on every restore).
        // Debounced (3s), so repeat inits cost nothing.
        scheduleAutoBackup();
      } catch (error) {
        _initPromise = null;
        throw error;
      }
    })();
  }
  await _initPromise;
}

export async function fanoutEncrypt(
  recipientAccountId: string,
  payload: PlaintextPayload,
  forceReestablish?: boolean,
  opts?: FanoutOpts,
): Promise<FanoutEntry[]> {
  await ensureE2eeReady();
  const crypto_ = getCrypto();
  // A device revoked server-side mid-session (another device used the revoke
  // UI) still has full local state — without this check it keeps sending but
  // silently stops receiving. Reclaim the registration (same identity) before
  // the fan-out so the device is back in the served bundle. CONTENT sends
  // only (opts === undefined): protocol pings (resend/backfill requests) must
  // NOT resurrect a revoked device on their own — a deliberate full-revoke
  // would never stick while a background poll keeps firing protocol fan-outs.
  if (!opts) await crypto_.reRegisterIfRevoked();
  // H-3: fold any pending per-target user acknowledgments for this contact
  // into the fanout. The allowance is ONE-SHOT: it is consumed (deleted)
  // after the first successful encrypt, so a stale ack can never silently
  // approve a later, different key change.
  const allowances = await loadIdentityAllowances(recipientAccountId);
  const mergedOpts: FanoutOpts | undefined =
    allowances.length > 0
      ? {
          ...opts,
          allowIdentityKeyChangeFor: [
            ...(opts?.allowIdentityKeyChangeFor ?? []),
            ...allowances,
          ],
        }
      : opts;
  const out = await crypto_.encryptMultiDevice(
    recipientAccountId,
    payload,
    forceReestablish,
    mergedOpts,
  );
  if (allowances.length > 0) {
    await clearIdentityAllowances(recipientAccountId);
  }
  return out;
}

// H-3: pending (not yet consumed) per-target acknowledgments for a contact,
// in the FanoutOpts allowance shape. Key-bound: the crypto layer honors an
// entry only while the server still serves the acknowledged key.
async function loadIdentityAllowances(
  accountId: string,
): Promise<IdentityChangeAllowance[]> {
  try {
    const acks = await getCrypto().store.loadIdentityChangeAcks(accountId);
    return acks.map((a) => ({
      accountId,
      deviceId: a.deviceId,
      identityKey: a.key,
    }));
  } catch {
    return [];
  }
}

async function clearIdentityAllowances(accountId: string): Promise<void> {
  try {
    const acks = await getCrypto().store.loadIdentityChangeAcks(accountId);
    for (const a of acks) {
      await getCrypto().store.removeIdentityChangeAck(accountId, a.deviceId);
    }
  } catch {
    /* best-effort */
  }
}

// H-3: SKDM distribution variants of the above — group paths merge the
// pending acknowledgments of EVERY member account (the distribution throws
// for the first unacknowledged member device).
async function loadGroupIdentityAllowances(
  memberAccountIds: string[],
): Promise<IdentityChangeAllowance[]> {
  const out: IdentityChangeAllowance[] = [];
  for (const id of memberAccountIds) {
    out.push(...(await loadIdentityAllowances(id)));
  }
  return out;
}

async function clearGroupIdentityAllowances(
  memberAccountIds: string[],
): Promise<void> {
  for (const id of memberAccountIds) {
    await clearIdentityAllowances(id);
  }
}

// MD-h: flush the durable own-sibling backfill outbox — re-encrypt message
// copies our own sibling devices missed at send time (their session was
// throttled/unestablishable) so they can be re-delivered. Returns the copies
// with the thread each belongs to; the caller POSTs them and then calls
// confirmSiblingBackfill() per copy on POST success (two-phase drain). This is
// best-effort background recovery: it never initializes E2EE just to flush and
// swallows a not-ready state, returning [].
export async function flushSiblingBackfills(): Promise<
  SiblingBackfillFanout[]
> {
  if (!isWasmSupported()) return [];
  try {
    await ensureE2eeReady();
  } catch {
    return [];
  }
  return getCrypto().flushSiblingBackfill();
}

// Durable retry for sibling copies whose POST failed AFTER the recipient's
// copy delivered — the send is already a success; the owed copies re-deliver
// through the normal MD-h flush. Best-effort like its siblings above.
export async function enqueueSiblingBackfill(
  recipientAccountId: string,
  payload: PlaintextPayload,
  deviceIds: number[],
): Promise<void> {
  if (!isWasmSupported()) return;
  try {
    await ensureE2eeReady();
  } catch {
    return;
  }
  await getCrypto().enqueueSiblingBackfill(
    recipientAccountId,
    payload,
    deviceIds,
  );
}

export async function confirmSiblingBackfill(
  threadAccountId: string,
  deviceId: number,
): Promise<void> {
  if (!isWasmSupported()) return;
  try {
    await ensureE2eeReady();
  } catch {
    return;
  }
  await getCrypto().confirmSiblingBackfillDelivered(threadAccountId, deviceId);
}

// MD-h repair: stamp the primary send's server-minted group_key onto any
// queued sibling-backfill copies for that thread (they were enqueued during
// the fan-out, before the POST returned). Best-effort like the rest of the
// backfill path: never initializes E2EE just to stamp, swallows not-ready.
export async function stampSiblingBackfillGroupKey(
  threadAccountId: string,
  groupKey: string,
): Promise<void> {
  if (!isWasmSupported()) return;
  try {
    await ensureE2eeReady();
  } catch {
    return;
  }
  await getCrypto().stampSiblingBackfillGroupKey(threadAccountId, groupKey);
}

export async function decryptInbound(
  senderAccountId: string,
  ciphertextB64: string,
  type: number,
  senderDeviceId = 1,
): Promise<PlaintextPayload> {
  await ensureE2eeReady();
  return getCrypto().decryptMessage(
    senderAccountId,
    ciphertextB64,
    type,
    senderDeviceId,
  );
}

export async function encryptAttachmentBytes(
  plain: ArrayBuffer,
): Promise<{ ciphertext: ArrayBuffer; key: string; iv: string }> {
  await ensureE2eeReady();
  const out = await getCrypto().encryptBytes(plain);
  return { ciphertext: out.bytes, key: out.key, iv: out.iv };
}

export async function decryptAttachmentBytes(
  ciphertext: ArrayBuffer,
  key: string,
  iv: string,
): Promise<ArrayBuffer> {
  await ensureE2eeReady();
  return getCrypto().decryptBytes(ciphertext, key, iv);
}

export async function rememberSent(
  messageId: string,
  payload: PlaintextPayload,
): Promise<void> {
  await ensureE2eeReady();
  await getCrypto().rememberSentMessage(messageId, payload);
}

// Called on user-initiated actions (DM thread open, send). Detects mid-session
// IDB prekey loss (browser storage eviction, manual clear) and replaces server
// prekey material so incoming senders get a bundle we can decrypt.
// Debounced internally — safe to call on every action.
export async function checkKeyMaterialHealth(): Promise<void> {
  if (!_initPromise) return; // not yet initialized — init will cover the check
  try {
    await getCrypto().checkKeyMaterialHealth();
  } catch {
    // best-effort: if the check itself fails, don't surface an error
  }
}

export async function recallSent(
  messageId: string,
): Promise<PlaintextPayload | undefined> {
  await ensureE2eeReady();
  return getCrypto().recallSentMessage(messageId);
}

export async function rememberInbound(
  messageId: string,
  payload: PlaintextPayload,
): Promise<void> {
  await ensureE2eeReady();
  await getCrypto().rememberInboundMessage(messageId, payload);
}

export async function recallInbound(
  messageId: string,
): Promise<PlaintextPayload | undefined> {
  await ensureE2eeReady();
  return getCrypto().recallInboundMessage(messageId);
}

// DRH-5: durable resend-replacement cache (keyed by the failed message's
// logical id). rememberReplacement is called on the recipient when a resent
// message (carrying resend_of_id) arrives; recallReplacement lets the original
// failed bubble resolve from it after a reload.
export async function rememberReplacement(
  failedKey: string,
  payload: PlaintextPayload,
): Promise<void> {
  await ensureE2eeReady();
  await getCrypto().rememberReplacementMessage(failedKey, payload);
}

export async function recallReplacement(
  failedKey: string,
): Promise<PlaintextPayload | undefined> {
  await ensureE2eeReady();
  return getCrypto().recallReplacementMessage(failedKey);
}

// DRH-5c: user-initiated safe re-establishment (no removeSession, no plaintext
// deletion). Returns the number of peer devices re-established.
export async function resetSecureSession(
  peerAccountId: string,
): Promise<number> {
  await ensureE2eeReady();
  return getCrypto().resetSecureSession(peerAccountId);
}

// DRH-4: per-conversation sequence/ack persistence (ids only). Sender remembers
// the peer's highest contiguously-decrypted ack to pick proactive resends;
// recipient remembers the last ack it sent to throttle duplicates.
export async function rememberPeerAck(
  peerAccountId: string,
  ackId: string,
): Promise<void> {
  await ensureE2eeReady();
  await getCrypto().rememberPeerAck(peerAccountId, ackId);
}

export async function recallPeerAck(
  peerAccountId: string,
): Promise<string | undefined> {
  await ensureE2eeReady();
  return getCrypto().recallPeerAck(peerAccountId);
}

export async function rememberSentAck(
  peerAccountId: string,
  ackId: string,
): Promise<void> {
  await ensureE2eeReady();
  await getCrypto().rememberSentAck(peerAccountId, ackId);
}

export async function recallSentAck(
  peerAccountId: string,
): Promise<string | undefined> {
  await ensureE2eeReady();
  return getCrypto().recallSentAck(peerAccountId);
}

export async function setupBackup(passphrase: string): Promise<void> {
  const { deriveBackupKey, enforceStrongPassphrase } = await import('./backup');
  enforceStrongPassphrase(passphrase);

  if (!isWasmSupported()) {
    throw new Error(
      "Your browser doesn't support WebAssembly, which is required for encrypted messaging. Please use a modern browser (Chrome 57+, Firefox 52+, Safari 11+, Edge 16+).",
    );
  }
  const crypto_ = getCrypto();
  // setupBackup IS the provisioning action for a needs-setup account, so it must
  // create the identity DIRECTLY rather than via ensureE2eeReady(), which
  // intentionally rejects with 'e2ee_provisioning_required' until the account is
  // provisioned (the F2/E2EE-HIGH-4 gate). Routing setup through that gate would
  // deadlock — the gate is only satisfied AFTER setup completes.
  const hadIdentity = !!(await crypto_.store.load('identity-record'));
  // force: a user clicking "Set up" must never be blocked by the failure
  // backoff that throttles the AUTOMATIC re-init loop.
  await crypto_.initIdentity({ force: true });

  if (!hadIdentity) {
    // Fresh provisioning: rows created before this identity existed are
    // permanently undecryptable here — they were encrypted to predecessor
    // identities / one-time prekeys this device never held. Record the same
    // history cutoff the restore path uses so dm_message HIDES those rows
    // instead of showing "couldn't decrypt" and firing resend requests whose
    // force-reestablish answers storm type-3 session establishments and race
    // the very messages the peer can actually read (e2e Phase-7 stall).
    await crypto_.store.save('restored-at', Date.now());
  }

  const saltBytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const key = await deriveBackupKey(passphrase, saltBytes);

  const saltB64 = bytesToB64(saltBytes);
  const keyB64 = bytesToB64(new Uint8Array(key));
  await crypto_.store.storeBackupCreds({ key: keyB64, salt: saltB64 });

  // Manual setup is a deliberate force-overwrite: no if_match_updated_at
  // precondition (M-12). The user just claimed backup ownership for THIS
  // device — clear any prior 409 stand-down and adopt the new updated_at.
  const updatedAt = await crypto_.uploadBackupWithKey(key, saltB64);
  await crypto_.store.remove('backup-auto-disabled');
  if (updatedAt) await crypto_.store.save('backup-updated-at', updatedAt);
}

// Provision THIS browser as an additional ("linked") device for the account,
// instead of restoring/adopting the existing device identity. Generates a fresh
// identity, registers as the next device_id, and marks the device linked so the
// setup gate treats it as ready WITHOUT owning the account backup (linked
// devices must not auto-backup — that would overwrite the primary's backup).
export async function linkNewDevice(): Promise<number> {
  const crypto_ = getCrypto();
  const deviceId = await crypto_.linkNewDevice();
  return deviceId;
}

export async function restoreBackupWithPassphrase(
  passphrase: string,
): Promise<void> {
  const { decryptBackupV2 } = await import('./backup');
  // Don't enforce passphrase strength on restore — the backup was already
  // created with whatever passphrase the user chose. Rejecting a valid
  // password here would lock users out of their backup (R19 fix).

  const crypto_ = getCrypto();
  const resp = await apiRequest<{
    ciphertext: string;
    kdf_salt: string;
    kdf_params: { algorithm: string; m?: number; t?: number; p?: number };
    updated_at?: string;
  }>('GET', 'v1/e2ee/backup');

  if (resp.kdf_params?.algorithm && resp.kdf_params.algorithm !== 'argon2id') {
    throw new Error(
      'Server-stored backup uses an older format (v1). Please re-set-up backup from a device that has your current keys.',
    );
  }

  const snapshot = (await decryptBackupV2(
    b64ToArrayBuffer(resp.ciphertext),
    passphrase,
    resp.kdf_salt,
    resp.kdf_params as {
      algorithm: 'argon2id';
      m: number;
      t: number;
      p: number;
    },
  )) as Record<string, unknown>;

  // R49 / MD-c: NEVER collapse onto a device_id that is still registered by a
  // DIFFERENT live client. The backup carries the primary's own device_id (the
  // plain `myDeviceId` key). Three cases:
  const backedUpDeviceId =
    typeof snapshot['myDeviceId'] === 'number'
      ? (snapshot['myDeviceId'] as number)
      : undefined;
  const hasLocalIdentity = !!(await crypto_.store.load('identity-record'));
  const localMyDeviceId = await crypto_.store.getMyDeviceId();

  // (1) SAME physical device recovering only its lost backup creds. If THIS
  // browser already holds the identity for the backed-up device_id (its creds
  // were cleared/swallowed while the identity survived — detectE2eeSetupState
  // returns 'needs-restore' in that window), it is NOT a second live client. Do
  // NOT hydrate (the local identity-record is more up-to-date than the backup
  // snapshot; overwriting it would regress prekey counters → R47) and do NOT
  // link (that would orphan this device). Just re-derive + store the creds so
  // backup ownership is recovered; identity/sessions/device_id stay intact.
  if (
    hasLocalIdentity &&
    backedUpDeviceId !== undefined &&
    localMyDeviceId === backedUpDeviceId
  ) {
    const { deriveBackupKey } = await import('./backup');
    const keyBytes = await deriveBackupKey(
      passphrase,
      new Uint8Array(b64ToArrayBuffer(resp.kdf_salt)),
    );
    await crypto_.store.storeBackupCreds({
      key: bytesToB64(new Uint8Array(keyBytes)),
      salt: resp.kdf_salt,
    });
    // Backup ownership recovered — clear any 409 stand-down and adopt the
    // server's updated_at for the next preconditioned PUT (M-12).
    await crypto_.store.remove('backup-auto-disabled');
    if (typeof resp.updated_at === 'string') {
      await crypto_.store.save('backup-updated-at', resp.updated_at);
    }
    _initPromise = null;
    _instance = null;
    return;
  }

  // (2) A FRESH client (no local identity for this id) whose backed-up
  // device_id is STILL registered would become a second live writer on that
  // device_id / double-ratchet (R48) — and across two physical machines there
  // is no shared Web Lock (DRH-3 only covers same-browser tabs), so they
  // diverge and decryption fails. Provision THIS client as a genuine NEW device
  // (distinct device_id + own identity via linkNewDevice) and claim backup
  // ownership from the entered passphrase so it can still back up / act as
  // primary.
  if (backedUpDeviceId !== undefined) {
    // Fail SAFE: default to "still registered" (→ non-collapsing link path).
    // Only a well-formed device list that DEFINITIVELY lacks the id downgrades
    // to the reclaim path — a malformed/unexpected shape or a thrown error must
    // never silently take the collapse-prone reclaim.
    let stillRegistered = true;
    try {
      const mine = await apiRequest<{ devices?: { device_id: number }[] }>(
        'GET',
        'v1/e2ee/identity/mine',
      );
      if (Array.isArray(mine?.devices)) {
        stillRegistered = mine.devices.some(
          (d) => d.device_id === backedUpDeviceId,
        );
      }
    } catch {
      stillRegistered = true;
    }
    if (stillRegistered) {
      if (hasLocalIdentity) {
        // MED-5 fail-closed: this client ALREADY holds a local identity for a
        // DIFFERENT device_id (e.g. a linked secondary whose creds and
        // 'linked-device' flag were lost, so detectE2eeSetupState offered the
        // restore flow). Linking over it would mint a fresh identity and
        // orphan every session peers hold for the existing device_id — the
        // exact key loss MED-5 warns about. Refuse with a retryable error
        // instead of silently destroying valid local keys; the user can still
        // replace the device deliberately via a local E2EE reset.
        //
        // Exception: a device that already owns backup creds was ITSELF
        // provisioned by a restore→link from this backup — a repeated restore
        // is the gate-retry race and must stay idempotent (linkNewDevice
        // returns the existing assignment, creds are just refreshed).
        const existingCreds = await crypto_.store.loadBackupCreds();
        if (!existingCreds) {
          throw new Error(
            'This device already has its own encryption identity (a different device id). Refusing to overwrite it with a backup restore. Reset E2EE locally on this device first if it should be replaced.',
          );
        }
      }
      const { deriveBackupKey } = await import('./backup');
      await crypto_.linkNewDevice();
      const keyBytes = await deriveBackupKey(
        passphrase,
        new Uint8Array(b64ToArrayBuffer(resp.kdf_salt)),
      );
      await crypto_.store.storeBackupCreds({
        key: bytesToB64(new Uint8Array(keyBytes)),
        salt: resp.kdf_salt,
      });
      // A real primary/backup-owner now, not a passive linked secondary.
      await crypto_.store.save('linked-device', false);
      await crypto_.store.remove('backup-auto-disabled');
      if (typeof resp.updated_at === 'string') {
        await crypto_.store.save('backup-updated-at', resp.updated_at);
      }
      _initPromise = null;
      _instance = null;
      return;
    }
  }

  // (3) The backed-up device is ABSENT (a fully-revoked account) and this is a
  // fresh client — the classic identity-reclaim restore is safe (nothing to
  // collapse with). MED-5: hydrating over an EXISTING local identity would
  // regress prekey counters (R47) and orphan sessions established since the
  // backup, so only a truly fresh client may take this path — same fail-closed
  // refusal as branch (2) above.
  if (hasLocalIdentity) {
    throw new Error(
      'This device already has its own encryption identity (a different device id). Refusing to overwrite it with a backup restore. Reset E2EE locally on this device first if it should be replaced.',
    );
  }
  await crypto_.store.hydrate(snapshot);
  await crypto_.store.storeBackupCreds({
    salt: resp.kdf_salt,
  });

  // Restore cutoff: rows created before the restored backup was taken are
  // permanently undecryptable on this device — the one-time prekeys they were
  // encrypted to were consumed on the lost device (never in the backup), and
  // the backed-up ratchet state has already advanced past them. dm_message
  // hides those rows instead of showing "couldn't decrypt" and firing resend
  // requests nobody can satisfy for us. Local-only (excluded from dump());
  // re-derived from the backup's updated_at on every restore.
  if (typeof resp.updated_at === 'string') {
    // Microsecond fractions are rejected by Date.parse on some engines.
    const parsed = parseServerTimestamp(resp.updated_at);
    if (!Number.isNaN(parsed)) {
      // L-15: the timestamp is server-controlled — a far-future value would
      // hide ALL history on the restored device. Clamp to now.
      const cutoff = Math.min(Date.now(), parsed);
      await crypto_.store.save('restored-at', cutoff);
      // M-12: adopt the backup's updated_at so our next auto-backup PUT
      // carries the right if_match_updated_at precondition.
      await crypto_.store.save('backup-updated-at', resp.updated_at);
    }
  }

  // Reset the singleton so the next call to getCrypto() re-creates it with
  // the hydrated store, and ensureE2eeReady() will re-run initIdentity() on
  // the restored state.
  _initPromise = null;
  _instance = null;
}

let _backupDebounceTimer: ReturnType<typeof setTimeout> | null = null;
export function scheduleAutoBackup(): void {
  if (_backupDebounceTimer) clearTimeout(_backupDebounceTimer);
  _backupDebounceTimer = setTimeout(() => {
    _backupDebounceTimer = null;
    void autoBackupNow().catch(() => {
      /* silently fail; will retry on next activity */
    });
  }, 3000);
}

// 2026-08-08 storm: a non-conflict PUT failure (422 "Backup too large", 5xx,
// network) used to retry on EVERY activity tick — a 10MiB Argon2id
// re-encryption + upload every few seconds. Failures now arm a cooldown;
// a later success clears it. Deliberately NOT the permanent 409 stand-down:
// the failing condition can heal server-side (cap raise, blob shrink).
const BACKUP_FAILURE_COOLDOWN_MS = 30 * 60 * 1000;

export async function autoBackupNow(): Promise<void> {
  await ensureE2eeReady();
  const crypto_ = getCrypto();
  // M-12: this device lost the backup-writer race (a 409 told us another
  // device owns a newer backup) — auto-backup stays off until a manual
  // setupBackup force-overwrites or the user restores.
  if (await crypto_.store.load<boolean>('backup-auto-disabled')) return;
  const failUntil = await crypto_.store.load<number>('backup-fail-until');
  if (typeof failUntil === 'number' && Date.now() < failUntil) return;
  const creds = await crypto_.store.loadBackupCreds();
  // After passphrase restore, only salt is stored (key must be re-derived on
  // demand). Skip auto-backup if the pre-derived key is absent (N08 fix).
  if (!creds?.key) return;
  const keyBytes = b64ToArrayBuffer(creds.key);
  // M-12: write precondition — PUT only over the backup WE last saw. A 409
  // means another device is the live backup owner: stand down (disable this
  // device's auto-backup) instead of thrashing last-writer-wins.
  const lastSeen = await crypto_.store.load<string>('backup-updated-at');
  try {
    const updatedAt = await crypto_.uploadBackupWithKey(
      keyBytes,
      creds.salt,
      lastSeen ? { ifMatchUpdatedAt: lastSeen } : undefined,
    );
    if (updatedAt) await crypto_.store.save('backup-updated-at', updatedAt);
    await crypto_.store.save('backup-fail-until', 0);
  } catch (err) {
    if (
      (err as { code?: string } | null | undefined)?.code ===
      'e2ee_backup_conflict'
    ) {
      await crypto_.store.save('backup-auto-disabled', true);
      const newer = (err as { updatedAt?: string }).updatedAt;
      if (newer) await crypto_.store.save('backup-updated-at', newer);
      console.warn(
        '[E2EE] Auto-backup disabled: another device owns a newer backup (409).',
      );
      return;
    }
    await crypto_.store.save(
      'backup-fail-until',
      Date.now() + BACKUP_FAILURE_COOLDOWN_MS,
    );
    console.warn(
      '[E2EE] Auto-backup failed; backing off 30 minutes before retrying.',
      err,
    );
    throw err;
  }
}

export async function hasLocalBackupCreds(): Promise<boolean> {
  await ensureE2eeReady();
  const creds = await getCrypto().store.loadBackupCreds();
  return !!creds;
}

// Timestamp (ms epoch) of the backup this device was restored from, or null
// when this device holds its original keys. Messages older than the cutoff
// are permanently undecryptable here and are hidden by dm_message rather
// than shown as decrypt failures. See restoreBackupWithPassphrase branch (3).
export async function getRestoreCutoff(): Promise<number | null> {
  const cutoff = await getCrypto().store.load<number>('restored-at');
  return typeof cutoff === 'number' ? cutoff : null;
}

export async function hasRemoteBackup(): Promise<boolean> {
  return getCrypto().hasRemoteBackup();
}

export type E2eeSetupState = 'ready' | 'needs-setup' | 'needs-restore';

// Detect what E2EE gate state the user is in — without initializing WASM.
// 'ready'         → keys in IDB + backup creds set → proceed normally
// 'needs-setup'   → no backup creds (first login or existing user without backup)
// 'needs-restore' → no local keys but server has a backup → new device
export async function detectE2eeSetupState(): Promise<E2eeSetupState> {
  if (!isWasmSupported()) {
    throw new Error(
      "Your browser doesn't support WebAssembly, which is required for encrypted messaging.",
    );
  }
  const store = getCrypto().store;
  const readLocalState = async (): Promise<'ready' | 'uncertain'> => {
    const hasIdentity = !!(await store.load('identity-record'));
    if (!hasIdentity) return 'uncertain';
    const creds = await store.loadBackupCreds();
    if (creds) return 'ready';
    // Linked (secondary) devices have their own identity but intentionally do
    // NOT own the account backup creds — they are ready without a backup.
    const isLinked = await store.load<boolean>('linked-device');
    if (isLinked) return 'ready';
    // Identity exists but no local backup creds — check if server has a backup
    // before concluding they need setup (which would overwrite their existing keys).
    return 'uncertain';
  };
  let local = await readLocalState();
  if (local !== 'ready') {
    // Retry once before concluding anything: under heavy IDB load (streaming
    // decrypts + epoch-triggered mem-cache clears) a transient read can come
    // back empty for a device whose keys are actually intact — concluding
    // non-'ready' here makes ensureE2eeReady() throw e2ee_provisioning_required
    // mid-conversation (gc-realtime flake). A single re-read absorbs that
    // without weakening the gate: genuinely unprovisioned devices still fall
    // through to the server-side backup check below.
    await new Promise((resolve) => setTimeout(resolve, 250));
    local = await readLocalState();
  }
  if (local === 'ready') return 'ready';
  const hasBackup = await getCrypto().hasRemoteBackup();
  return hasBackup ? 'needs-restore' : 'needs-setup';
}

export async function destroyE2ee(): Promise<void> {
  // Cancel any pending auto-backup so it doesn't fire after logout and
  // accidentally re-initialize E2EE on a now-logged-out account (N30 fix).
  if (_backupDebounceTimer) {
    clearTimeout(_backupDebounceTimer);
    _backupDebounceTimer = null;
  }
  if (_instance) {
    await _instance.destroy();
  }
  _instance = null;
  _initPromise = null;
  // D-12: wipe E2EE localStorage residue. The SKDM distribution memos
  // (`e2ee_dist:{groupChatId}`, written by actions/group_chats.ts) survive
  // IndexedDB wipes — after logout they would leak group/distId metadata on
  // a shared machine and poison the next account's distribution bookkeeping.
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith('e2ee_')) doomed.push(k);
    }
    for (const k of doomed) localStorage.removeItem(k);
  } catch {
    // localStorage unavailable — nothing more to wipe.
  }
}

// --- Safety number helpers (identity verification, closes R1 MITM gap) ---

export async function listContactDevices(
  contactAccountId: string,
): Promise<Array<{ device_id: number; identity_key: string }>> {
  await ensureE2eeReady();
  return getCrypto().listContactDevices(contactAccountId);
}

export async function generateSafetyNumber(
  contactAccountId: string,
  deviceId = 1,
): Promise<SafetyNumber> {
  await ensureE2eeReady();
  return getCrypto().generateSafetyNumber(contactAccountId, deviceId);
}

export async function verifySafetyNumber(
  contactAccountId: string,
  scanned: Uint8Array,
  deviceId = 1,
): Promise<boolean> {
  await ensureE2eeReady();
  return getCrypto().verifySafetyNumber(contactAccountId, scanned, deviceId);
}

export async function checkIdentityKeyChanged(
  contactAccountId: string,
): Promise<IdentityKeyChange> {
  await ensureE2eeReady();
  return getCrypto().checkIdentityKeyChanged(contactAccountId);
}

// H-3/H-4: user acknowledged the currently-served identity key for
// (contact, device) — typically after verifying its safety number. Writes
// the PER-DEVICE pin (the legacy per-account slot is never consulted once
// per-device pins exist), admits the device into the seen-device set (H-5
// gate), and records a one-shot per-target acknowledgment so the next send
// re-establishes onto the acknowledged key without hitting the
// identity_key_changed block.
export async function acknowledgeIdentityKeyChange(
  contactAccountId: string,
  trustedKey: string,
  deviceId = 1,
): Promise<void> {
  await ensureE2eeReady();
  const store = getCrypto().store;
  await store.saveRemoteIdentityKey(contactAccountId, trustedKey, deviceId);
  const seen = await store.loadSeenDeviceIds(contactAccountId);
  if (!seen.includes(deviceId)) {
    await store.saveSeenDeviceIds(contactAccountId, [...seen, deviceId]);
  }
  await store.saveIdentityChangeAck(contactAccountId, deviceId, trustedKey);
}

// H-4: key-bound verification badge helpers for the safety-number UI. Both
// go through the ACCOUNT-SCOPED store of the crypto singleton (the account-
// scoped IndexedDB wiped by destroyAll on logout) — never a default-named
// store, which would strand verification state outside the wiped database.
export async function isSafetyNumberVerifiedForKey(
  contactAccountId: string,
  deviceId: number,
  currentKey: string,
): Promise<boolean> {
  return getCrypto().store.isSafetyNumberVerifiedForKey(
    contactAccountId,
    deviceId,
    currentKey,
  );
}

export async function markSafetyNumberVerified(
  contactAccountId: string,
  currentKey: string,
  deviceId = 1,
): Promise<void> {
  await getCrypto().store.saveSafetyNumberVerified(
    contactAccountId,
    currentKey,
    deviceId,
  );
  // Verification of the CURRENT key is also the acknowledgment that unblocks
  // sends to this device (H-3) and admits it into the seen set (H-5).
  await acknowledgeIdentityKeyChange(contactAccountId, currentKey, deviceId);
}

export async function expireSessions(): Promise<number> {
  await ensureE2eeReady();
  return getCrypto().expireSessions();
}

export async function enforceSessionLimit(): Promise<number> {
  await ensureE2eeReady();
  return getCrypto().enforceSessionLimit();
}

// --- Group E2EE (Sender Key) ---

export async function encryptGroupMessage(
  distributionId: string,
  payload: PlaintextPayload,
): Promise<{ ciphertextB64: string; type: number }> {
  await ensureE2eeReady();
  return getCrypto().encryptGroupMessage(distributionId, payload);
}

// M-2: `expectedDistributionId` is REQUIRED — the thread's current
// distribution_id. The ciphertext is rejected with an E2eeError
// (`context.code === 'group_distid_mismatch'`) unless the distId embedded in
// its header matches, binding group ciphertexts to their group/epoch. The
// result carries the sender-chain `iteration` for replay dedupe (L-12).
export async function decryptGroupMessage(
  senderAccountId: string,
  senderDeviceId: number,
  ciphertextB64: string,
  expectedDistributionId: string,
): Promise<GroupDecryptResult> {
  await ensureE2eeReady();
  return getCrypto().decryptGroupMessage(
    senderAccountId,
    senderDeviceId,
    ciphertextB64,
    expectedDistributionId,
  );
}

// M-6b: purge all local key material for an OLD group epoch. Call after a
// successful create+distribute under a NEW distId, with the OLD distId.
export async function purgeGroupEpoch(distributionId: string): Promise<void> {
  await ensureE2eeReady();
  return getCrypto().purgeGroupEpoch(distributionId);
}

// M-2/M-6b: send-side distId hygiene. Record a server-served distId for a
// group; 'rollback' (previously-seen-and-superseded, or owned by a DIFFERENT
// group) must refuse to send/distribute. See signal_store.recordGroupDistId.
export async function recordGroupDistIdSeen(
  groupChatId: string,
  distId: string,
): Promise<'new' | 'seen' | 'rollback'> {
  return getCrypto().store.recordGroupDistId(groupChatId, distId);
}

export async function loadGroupDistIdHistory(
  groupChatId: string,
): Promise<string[]> {
  return getCrypto().store.loadGroupDistIdHistory(groupChatId);
}

export async function fetchAndProcessSenderKeys(
  groupChatId: string,
): Promise<boolean> {
  await ensureE2eeReady();
  return getCrypto().fetchAndProcessSenderKeys(groupChatId);
}

export async function uploadSenderKeyDistribution(
  groupChatId: string,
  distributionId: string,
  memberAccountIds: string[],
  opts?: IdentityChangeOpts,
): Promise<number> {
  await ensureE2eeReady();
  // H-3: merge pending one-shot per-member acknowledgments; consumed after
  // the first successful distribution.
  const allowances = await loadGroupIdentityAllowances(memberAccountIds);
  const mergedOpts: IdentityChangeOpts | undefined =
    allowances.length > 0
      ? {
          ...opts,
          allowIdentityKeyChangeFor: [
            ...(opts?.allowIdentityKeyChangeFor ?? []),
            ...allowances,
          ],
        }
      : opts;
  const out = await getCrypto().uploadSenderKeyDistribution(
    groupChatId,
    distributionId,
    memberAccountIds,
    mergedOpts,
  );
  if (allowances.length > 0) {
    await clearGroupIdentityAllowances(memberAccountIds);
  }
  return out;
}

export async function rekeyGroup(
  groupChatId: string,
  distributionId: string,
  remainingMemberAccountIds: string[],
  opts?: IdentityChangeOpts,
): Promise<void> {
  await ensureE2eeReady();
  const allowances = await loadGroupIdentityAllowances(
    remainingMemberAccountIds,
  );
  const mergedOpts: IdentityChangeOpts | undefined =
    allowances.length > 0
      ? {
          ...opts,
          allowIdentityKeyChangeFor: [
            ...(opts?.allowIdentityKeyChangeFor ?? []),
            ...allowances,
          ],
        }
      : opts;
  await getCrypto().rekeyGroup(
    groupChatId,
    distributionId,
    remainingMemberAccountIds,
    mergedOpts,
  );
  if (allowances.length > 0) {
    await clearGroupIdentityAllowances(remainingMemberAccountIds);
  }
}

// --- base64 helpers ---
function bytesToB64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + 0x8000) as unknown as number[],
    );
  }
  return btoa(s);
}

function b64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function b64ToArrayBuffer(b64: string): ArrayBuffer {
  const bytes = b64ToBytes(b64);
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}
